(() => {
  if (customElements.get('giesto-collection-stories')) return;

  const colorOrders = new Map();
  const productId = product => String(product.id || '').replace('gid://shopify/Product/', '');

  async function colorOrder(id) {
    const cached = globalThis.CSGA?.PC?.m?.get(id);
    if (cached?.products?.length) return cached.products.map(productId);
    if (!colorOrders.has(id)) {
      const pending = (async () => {
        try {
          const shop = globalThis.Shopify?.shop;
          if (!shop) return [];
          const url = new URL('/apps/renk-grubu/get-group', location.origin);
          url.searchParams.set('shop', shop);
          url.searchParams.set('product_id', id);
          const response = await fetch(url, { signal: AbortSignal.timeout(6000) });
          if (!response.ok) return [];
          const group = await response.json();
          const ids = (group.products || []).map(productId);
          for (const relatedId of ids) colorOrders.set(relatedId, Promise.resolve(ids));
          return ids;
        } catch { return []; }
      })();
      colorOrders.set(id, pending);
    }
    return colorOrders.get(id);
  }

  class GiestoCollectionStories extends HTMLElement {
    #request;
    #key;
    #loading = false;
    #observer;
    #events;
    #carouselEvents;
    #preload;
    #cachedSource;
    static observedAttributes = ['hidden'];

    connectedCallback() {
      this.#events = new AbortController();
      this.addEventListener('click', this.#onClick, { signal: this.#events.signal });
      if (!this.hidden) this.activate();
    }

    disconnectedCallback() {
      this.#request?.abort();
      this.#preload?.request.abort();
      this.#preload = null;
      this.#observer?.disconnect();
      this.#events?.abort();
      this.#carouselEvents?.abort();
      this.#loading = false;
      this.#key = null;
    }

    attributeChangedCallback() {
      if (this.isConnected && !this.hidden) this.activate();
    }

    #text(tr, en) { return this.dataset.locale === 'tr' ? tr : en; }

    #slug(value) {
      return String(value || '')
        .toLocaleLowerCase('tr')
        .replaceAll('ı', 'i')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }

    #fit() {
      if (!this.closest('.gac--basic-collection')) return '';
      const match = location.hash.match(/^#fit-([a-z0-9-]+)$/i);
      return match ? match[1].toLowerCase() : '';
    }

    #matchesFit(card, fit) {
      const aliases = {
        'kisa-kollu': ['kisa-kollu', 'kisa-kol', 'short-sleeve'],
        'uzun-kollu': ['uzun-kollu', 'uzun-kol', 'long-sleeve'],
      };
      const identity = this.#slug(`${card.dataset.storyTags || ''}|${card.dataset.storyTitle || ''}`);
      return (aliases[fit] || [fit]).some(token => identity.includes(token));
    }

    #url() {
      // A tagged collection is encoded in the pathname rather than the query.
      // Keep that pathname so editorial view shows the selected subtype instead
      // of silently fetching the entire parent collection.
      const collectionPath = location.pathname.includes('/collections/')
        ? location.pathname
        : this.dataset.collectionUrl;
      const url = new URL(collectionPath, location.origin);
      url.search = location.search;
      for (const key of ['page', 'section_id', 'sections']) url.searchParams.delete(key);
      url.searchParams.set('view', 'giesto-stories');
      const theme = globalThis.Shopify?.theme;
      if (theme?.id && theme.role !== 'main' && theme.role !== 'live') url.searchParams.set('preview_theme_id', theme.id);
      url.searchParams.sort();
      const fit = this.#fit();
      if (fit) url.hash = `fit-${fit}`;
      return url;
    }

    async #download(url, signal) {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Editorial page: ${response.status}`);
      return response.text();
    }

    prefetch() {
      // Only warm the first page on intent, never the whole catalog on visit.
      if (navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
      const url = this.#url();
      if (this.#preload?.key === url.href || (this.#key === url.href && (this.#loading || this.querySelector('.gac-story')))) return;
      this.#preload?.request.abort();
      const request = new AbortController();
      const key = url.href;
      url.searchParams.set('page', 1);
      this.#preload = { key, request, promise: this.#download(url, request.signal).catch(() => null) };
    }

    async #yield(signal) {
      // Give input and paint a turn between expensive DOM operations.
      if (globalThis.scheduler?.yield) await scheduler.yield();
      else await new Promise(resolve => setTimeout(resolve, 0));
      signal.throwIfAborted();
    }

    async activate() {
      const url = this.#url();
      const key = url.href;
      if (key === this.#key && (this.#loading || this.querySelector('.gac-story'))) return;
      this.#request?.abort();
      if (this.#preload && this.#preload.key !== key) {
        this.#preload.request.abort();
        this.#preload = null;
      }
      this.#observer?.disconnect();
      this.#carouselEvents?.abort();
      this.#carouselEvents = new AbortController();
      const request = new AbortController();
      this.#request = request;
      this.#key = key;
      this.#loading = true;
      const status = this.querySelector('[data-story-status]');
      const groups = this.querySelector('[data-story-groups]');
      if (!status || !groups) { this.#loading = false; this.#key = null; return; }
      const skeleton = this.querySelector('[data-story-loading]');
      const back = this.querySelector('[data-story-back]');
      groups.replaceChildren();
      delete this.dataset.productCount;
      delete this.dataset.familyCount;
      status.hidden = true;
      status.textContent = '';
      if (skeleton) skeleton.hidden = false;
      if (back) back.hidden = true;
      this.querySelector('[data-story-retry]').hidden = true;
      this.setAttribute('aria-busy', 'true');

      try {
        const fetchPage = async page => {
          const pageUrl = new URL(url);
          pageUrl.searchParams.set('page', page);
          const preload = page === 1 && this.#preload?.key === key ? this.#preload : null;
          let html = (preload && await preload.promise) || await this.#download(pageUrl, request.signal);
          await this.#yield(request.signal);
          let doc = new DOMParser().parseFromString(html, 'text/html');
          let result = doc.querySelector('[data-story-page]');
          // Shopify's development-theme redirect can consume the preview id
          // before its cookie is ready on the first automatic request. Retry
          // through the already-previewed local origin instead of showing an
          // error until the customer refreshes or changes views.
          if (!result && pageUrl.searchParams.has('preview_theme_id')) {
            pageUrl.searchParams.delete('preview_theme_id');
            html = await this.#download(pageUrl, request.signal);
            await this.#yield(request.signal);
            doc = new DOMParser().parseFromString(html, 'text/html');
            result = doc.querySelector('[data-story-page]');
          }
          if (!result) throw new Error('Missing editorial collection response');
          return result;
        };
        const loadPages = async () => {
          // The filtered total is already in the collection section. Start up
          // to four pages together instead of waiting for page one first.
          let count = Math.max(1, Math.ceil(Number(this.dataset.collectionCount || 0) / 100));
          const pages = [];
          for (let page = 1; page <= count;) {
            const batch = await Promise.all(Array.from({ length: Math.min(4, count - page + 1) }, (_, i) => fetchPage(page + i)));
            pages.push(...batch);
            page += batch.length;
            count = Number(pages[0].dataset.pages) || 1;
          }
          return pages.slice(0, count);
        };
        const loadCanonical = async () => {
          const canonical = new Map();
          // Group translated colourways using their original catalog identity.
          if (this.dataset.locale === 'tr') return canonical;
          const familyPath = this.dataset.source === 'search'
            ? '/collections/tum-urunler'
            : url.pathname.slice(url.pathname.indexOf('/collections/'));
          const familyUrl = new URL(familyPath, location.origin);
          familyUrl.searchParams.set('view', 'giesto-story-families');
          if (url.searchParams.has('preview_theme_id')) familyUrl.searchParams.set('preview_theme_id', url.searchParams.get('preview_theme_id'));
          let familyPages = 1;
          for (let page = 1; page <= familyPages; page++) {
            familyUrl.searchParams.set('page', page);
            const html = await this.#download(familyUrl, request.signal);
            await this.#yield(request.signal);
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const data = JSON.parse(doc.querySelector('[data-story-families]').textContent);
            familyPages = data.pages;
            for (const [id, title, color, type, vendor] of data.products) canonical.set(String(id), { title, color, type, vendor });
          }
          return canonical;
        };
        const sourceKey = new URL(url);
        sourceKey.hash = '';
        const initialCards = this.closest('giesto-store-collection')?.querySelectorAll('results-list [ref="grid"] > [data-story-product]') || [];
        const seed = document.createElement('div');
        // The normal collection response already contains these cards. Clone
        // them so grid view and editorial view retain independent controls.
        if (this.dataset.initialPage === '1' && this.dataset.initialPath === location.pathname) {
          for (const card of initialCards) {
            const copy = card.cloneNode(true);
            copy.className = 'gac-story__color';
            copy.removeAttribute('id');
            copy.removeAttribute('style');
            seed.append(copy);
          }
        }
        const cached = this.#cachedSource?.key === sourceKey.href ? this.#cachedSource : null;
        const seedComplete = seed.children.length > 0 && seed.children.length >= Number(this.dataset.collectionCount);
        const self = this;
        async function* sources() {
          if (cached) {
            yield [cached.pages.map(page => page.cloneNode(true)), cached.canonical];
            return;
          }
          if (seed.children.length) yield [[seed], new Map()];
          if (seedComplete && self.dataset.locale === 'tr') return;
          const needsCanonical = self.dataset.locale !== 'tr';
          if (seedComplete && !needsCanonical) return;
          const [pages, canonical] = await Promise.all([loadPages(), loadCanonical()]);
          request.signal.throwIfAborted();
          self.#cachedSource = { key: sourceKey.href, pages: pages.map(page => page.cloneNode(true)), canonical };
          yield [pages, canonical];
        }
        let revealed = false;
        for await (const [pages, canonical] of sources()) {
          await this.#yield(request.signal);
          const fit = this.#fit();
          const families = new Map();
          const seen = new Set();
          for (const page of pages) {
            for (const card of page.querySelectorAll('[data-story-product]')) {
              const id = card.dataset.storyProduct;
              if (seen.has(id)) continue;
              if (fit && !this.#matchesFit(card, fit)) continue;
              seen.add(id);
              const identity = canonical.get(id) || { title: card.dataset.storyTitle, color: card.dataset.storyColor, type: card.dataset.storyType, vendor: card.dataset.storyVendor };
              const model = globalThis.GiestoColorGroups?.model(identity.title, identity.color);
              const family = model ? JSON.stringify([identity.vendor, identity.type, model]) : `product:${id}`;
              card.dataset.storyFamily = family;
              if (!families.has(family)) families.set(family, []);
              families.get(family).push(card);
            }
          }
          if (!families.size && pages[0] === seed && !seedComplete) continue;
          this.#observer?.disconnect();
          this.#carouselEvents?.abort();
          this.#carouselEvents = new AbortController();
          groups.replaceChildren();
          if (!families.size) {
            status.hidden = false;
            if (back) back.hidden = false;
            status.textContent = this.#text('Ürün bulunamadı.', 'No products found.');
            continue;
          }
          this.#observer = new IntersectionObserver(entries => {
            for (const entry of entries) if (entry.isIntersecting) {
              this.#mountColors(entry.target);
              this.#observer.unobserve(entry.target);
            }
          }, { rootMargin: '1200px' });
          const ordered = this.#orderFamilies([...families.values()]);
          let index = 0;
          let deadline = performance.now() + 35;
          while (ordered.length) {
            const { cards, lead } = await ordered.shift();
            request.signal.throwIfAborted();
            const story = this.#makeStory(cards, index, 'spread', lead);
            groups.append(story);
            this.#observer.observe(story);
            if (index++ === 0) {
              if (fit) story.id = `fit-${fit}`;
              this.#mountColors(story);
              if (skeleton) skeleton.hidden = true;
              if (back) back.hidden = true;
              status.hidden = true;
            }
            // Reveal the first complete family before building off-screen rows.
            if (index === 1 || performance.now() > deadline) {
              await this.#yield(request.signal);
              deadline = performance.now() + 35;
            }
          }
          this.dataset.productCount = String(seen.size);
          this.dataset.familyCount = String(families.size);
          if (!revealed) this.dispatchEvent(new CustomEvent('giesto:stories-ready', { bubbles: true, detail: { fit } }));
          revealed = true;
        }
      } catch (error) {
        if (request.signal.aborted) return;
        console.error('[Giesto collection stories]', error);
        request.abort();
        const hasContent = !!groups.querySelector('.gac-story');
        if (!hasContent) this.#observer?.disconnect();
        this.#key = null;
        status.hidden = false;
        status.textContent = hasContent
          ? this.#text('Diğer ürünler yüklenemedi. Lütfen tekrar deneyin.', 'The remaining products could not load. Please try again.')
          : this.#text('Bu görünüm yüklenemedi. Lütfen tekrar deneyin.', 'This view could not load. Please try again.');
        if (back) back.hidden = false;
        this.querySelector('[data-story-retry]').hidden = false;
      } finally {
        if (this.#request === request) {
          this.#preload?.request.abort();
          this.#preload = null;
          this.#loading = false;
          if (skeleton) skeleton.hidden = true;
          this.removeAttribute('aria-busy');
        }
      }
    }

    #section(layout, label) {
      const section = document.createElement('section');
      section.className = `gac-story gac-story--${layout}`;
      section.dataset.storyLayout = layout;
      section.setAttribute('aria-label', label);
      return section;
    }

    #orderFamilies(families) {
      // Shopify's FW27 master collection chooses its representative product
      // across season pages, categories, all-products and search. Keep the
      // app order for the bottom colour cards and for Basic's presentation.
      // Both choices are restricted to products in the current filtered result.
      const isBasic = this.closest('.gac--basic-collection') || this.closest('giesto-collection-hub[data-mode="basic"]');
      const useSeasonOrder = !isBasic &&
        families.some(cards => cards.some(card => card.dataset.storySeason === 'fw27'));
      const seasonOrder = useSeasonOrder ? globalThis.GiestoColorGroups?.seasonOrder?.() || Promise.resolve([]) : Promise.resolve([]);
      let next = 0;
      const resolve = [];
      const pending = families.map((_, index) => new Promise(done => { resolve[index] = done; }));
      const worker = async () => {
        const seasonIds = await seasonOrder;
        while (next < families.length) {
          const index = next++;
          const cards = families[index];
          const ids = cards.length > 1 ? await colorOrder(cards[0].dataset.storyProduct) : [];
          const ranks = new Map(ids.map((id, rank) => [id, rank]));
          const sorted = cards.slice().sort((a, b) =>
            (ranks.get(a.dataset.storyProduct) ?? ids.length) - (ranks.get(b.dataset.storyProduct) ?? ids.length));
          const seasonLeadId = seasonIds.find(id => cards.some(card => card.dataset.storySeason === 'fw27' && card.dataset.storyProduct === id));
          resolve[index]({ cards: sorted, lead: cards.find(card => card.dataset.storyProduct === seasonLeadId) || sorted[0] });
        }
      };
      for (let index = 0; index < Math.min(6, families.length); index++) worker();
      return pending;
    }

    #prepareCard(card, packshot, colorCard = false) {
      const gallery = card.querySelector('[data-gac-gallery]');
      if (packshot) {
        // Image policy belongs to the product, including on mixed catalogue pages.
        // FW27 starts with its first photograph; Basic keeps its last photo.
        // Video previews are excluded from these small colour cards.
        const frames = [...gallery?.children || []];
        const photos = frames.filter(frame => frame.dataset.mediaType === 'image');
        const startingPhoto = card.dataset.storyColorImageOrder === 'first' ? photos[0] : photos.at(-1);
        if (startingPhoto) {
          gallery.prepend(startingPhoto);
          gallery.scrollLeft = 0;
          card.querySelector('[data-gac-gallery-prev]')?.setAttribute('disabled', '');
          card.querySelector('[data-gac-gallery-next]')?.toggleAttribute('disabled', frames.length < 2);
        }
      }
      for (const img of card.querySelectorAll('.gac-card__frame img')) {
        img.sizes = packshot ? '(min-width: 1025px) 19vw, 44vw' : '(min-width: 1025px) 25vw, 70vw';
      }
      const first = card.querySelector('.gac-card__frame img');
      if (first) first.alt = card.dataset.storyTitle;
      // These families are already resolved from the active collection. Avoid
      // an app rescan replacing them with unrelated or unfiltered products.
      if (colorCard) {
        // A color card already represents one choice in the resolved family.
        // Native Shopify selectors must not create a second family inside it.
        card.querySelectorAll('.gac-card__colour-groups, .gac-card__swatches').forEach(selector => selector.remove());
      } else {
        card.querySelector('[data-gac-swatch-sync]')?.closest('.gac-card__colour-groups')?.remove();
      }
      const favorite = card.querySelector('giesto-wishlist-toggle');
      if (favorite) {
        favorite.classList.add('gac-story__favorite');
        card.querySelector('.gac-card__media').append(favorite);
        card.querySelector('.gac-card__quick-divider')?.remove();
      }
      card.querySelector('.gac-card__meta').title = card.dataset.storyTitle;
      return card;
    }

    #makeLeadMeta(cards, lead = cards[0]) {
      const sourceMeta = lead.querySelector('.gac-card__meta');
      const href = sourceMeta?.getAttribute('href') || '#';
      const meta = document.createElement('header');
      meta.className = 'gac-story__lead-meta';

      const link = document.createElement('a');
      link.className = 'gac-story__lead-link';
      link.href = href;

      const title = document.createElement('span');
      title.className = 'gac-story__lead-title';
      title.textContent = lead.dataset.storyTitle;
      link.append(title);

      const sourcePrice = sourceMeta?.querySelector('.gac-card__price-row');
      if (sourcePrice) {
        const price = document.createElement('span');
        price.className = 'gac-story__lead-price';
        price.append(...sourcePrice.cloneNode(true).childNodes);
        link.append(price);
      }

      const swatches = document.createElement('div');
      swatches.className = 'gac-story__lead-swatches';
      swatches.setAttribute('role', 'list');
      swatches.setAttribute('aria-label', this.#text('Renk seçenekleri', 'Color options'));
      for (const card of cards) {
        const swatch = document.createElement('a');
        swatch.className = 'gac-story__lead-swatch';
        swatch.href = card.querySelector('.gac-card__meta')?.getAttribute('href') || href;
        swatch.title = card.dataset.storyColor || card.dataset.storyTitle;
        swatch.setAttribute('aria-label', card.dataset.storyTitle);
        swatch.setAttribute('role', 'listitem');
        if (card === lead) swatch.setAttribute('aria-current', 'true');
        const chip = document.createElement('span');
        chip.style.background = card.dataset.storyChip || '#d8d8d8';
        swatch.append(chip);
        swatches.append(swatch);
      }

      meta.append(link, swatches);
      return meta;
    }

    #makeStory(cards, index, layout, lead = cards[0]) {
      const story = this.#section(layout, lead.dataset.storyTitle);
      story.dataset.family = lead.dataset.storyFamily;
      const leadMeta = this.#makeLeadMeta(cards, lead);
      const hero = document.createElement('a');
      hero.className = 'gac-story__hero';
      hero.href = lead.querySelector('.gac-card__meta').getAttribute('href');
      hero.setAttribute('aria-label', lead.dataset.storyTitle);
      const mediaOrder = new Map((lead.dataset.storyMediaOrder || '').split(',').map((id, position) => [id, position]));
      const frames = [...lead.querySelectorAll('.gac-card__frame')].sort((a, b) =>
        (mediaOrder.get(a.dataset.mediaId) ?? mediaOrder.size) - (mediaOrder.get(b.dataset.mediaId) ?? mediaOrder.size));
      const media = frames.map(frame => frame.querySelector(':scope > img, :scope > video')).filter(Boolean);
      // Use the first two Admin media items in order, left then right. Card
      // galleries may move videos or packshots first; that doesn't reorder here.
      const heroMedia = media.slice(0, layout === 'portrait' ? 1 : 2);
      // Both halves show the same colourway; retain the actual photograph's
      // proportions when there is no landscape campaign image in our catalog.
      for (const source of heroMedia) {
        const img = source.cloneNode(true);
        if (img.tagName === 'IMG') img.alt = '';
        else img.setAttribute('aria-label', lead.dataset.storyTitle);
        img.sizes = heroMedia.length > 1 ? '(min-width: 1025px) 27vw, 50vw' : '(min-width: 1025px) 38vw, 100vw';
        img.loading = index === 0 ? 'eager' : 'lazy';
        img.fetchPriority = index === 0 ? 'high' : 'low';
        hero.append(img);
      }
      // Products without uploaded photography retain their existing placeholder.
      if (!hero.children.length) {
        const placeholder = lead.querySelector('.gac-card__placeholder');
        if (placeholder) hero.append(placeholder.cloneNode(true));
      }
      hero.classList.toggle('gac-story__hero--single', hero.children.length < 2);
      if (cards.length === 1) {
        const frame = document.createElement('div');
        frame.className = 'gac-story__hero-frame';
        const card = this.#prepareCard(lead, true);
        card.classList.add('gac-story__single-card');
        const favorite = card.querySelector('giesto-wishlist-toggle');
        if (favorite) card.append(favorite);
        const badges = card.querySelector('.gac-card__badges');
        if (badges) card.append(badges);
        card.querySelector('.gac-card__media')?.remove();
        card.querySelector('.gac-card__meta')?.remove();
        leadMeta.querySelector('.gac-story__lead-swatches')?.remove();
        frame.append(hero, card);
        story.append(leadMeta, frame);
      } else {
        const colors = this.#makeRail(cards.map(card => this.#prepareCard(card, true, true)), lead.dataset.storyProduct, true);
        if (cards.length <= 2) colors.classList.add('gac-story__colors--few');
        story.append(leadMeta, hero, colors);
      }
      return story;
    }

    #makeProductRow(families, layout) {
      const story = this.#section(layout, this.#text('Koleksiyon ürünleri', 'Collection products'));
      const items = families.map(cards => this.#makeFamily(cards, layout === 'rail'));
      if (layout === 'rail') {
        story.append(this.#makeRail(items, families[0][0].dataset.storyProduct, false));
      } else {
        const grid = document.createElement('ul');
        grid.className = 'gac-story__grid';
        grid.setAttribute('role', 'list');
        const template = document.createElement('template');
        template.dataset.storyMount = '';
        template.content.append(...items);
        story.append(grid, template);
      }
      return story;
    }

    #makeFamily(cards, packshot) {
      const item = document.createElement('li');
      item.className = 'gac-story__family';
      item.dataset.family = cards[0].dataset.storyFamily;
      const active = document.createElement('div');
      active.dataset.storyActive = '';
      const variants = document.createElement('template');
      variants.dataset.storyVariants = '';
      for (const source of cards) {
        // A family is the list item; its colour cards are ordinary containers.
        const card = document.createElement('div');
        for (const attr of source.attributes) card.setAttribute(attr.name, attr.value);
        card.append(...source.childNodes);
        this.#prepareCard(card, packshot, cards.length > 1);
        if (!active.firstChild) active.append(card);
        else variants.content.append(card);
      }
      item.append(active, variants);
      if (cards.length > 1) {
        const swatches = document.createElement('div');
        swatches.className = 'gac-story__swatches';
        swatches.setAttribute('role', 'group');
        swatches.setAttribute('aria-label', this.#text('Renk seçenekleri', 'Color options'));
        for (const [index, card] of cards.entries()) {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.storyColorId = card.dataset.storyProduct;
          button.setAttribute('aria-label', card.dataset.storyTitle);
          button.setAttribute('aria-pressed', String(index === 0));
          button.title = card.dataset.storyColor || card.dataset.storyTitle;
          const chip = document.createElement('span');
          chip.style.background = card.dataset.storyChip || '#d8d8d8';
          button.append(chip);
          swatches.append(button);
        }
        item.append(swatches);
      }
      return item;
    }

    #makeRail(items, id, colors) {
      const rail = document.createElement('div');
      rail.className = 'gac-story__colors';
      const controls = document.createElement('div');
      controls.className = 'gac-story__controls';
      const counter = document.createElement('span');
      counter.className = 'gac-story__counter';
      const prev = this.#arrow('previous', colors);
      const next = this.#arrow('next', colors);
      controls.append(counter, prev, next);
      const track = document.createElement('ul');
      track.className = 'gac-story__track';
      track.setAttribute('role', 'list');
      track.setAttribute('aria-label', colors ? this.#text('Renk seçenekleri', 'Color options') : this.#text('Koleksiyon ürünleri', 'Collection products'));
      track.id = `gac-story-colors-${id}`;
      prev.setAttribute('aria-controls', track.id);
      next.setAttribute('aria-controls', track.id);
      const template = document.createElement('template');
      template.dataset.storyMount = '';
      template.content.append(...items);
      rail.append(controls, track, template);
      return rail;
    }

    #arrow(direction, colors) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'gac-story__arrow';
      button.dataset.storyArrow = direction;
      button.setAttribute('aria-label', direction === 'next'
        ? (colors ? this.#text('Sonraki renkler', 'Next colors') : this.#text('Sonraki ürünler', 'Next products'))
        : (colors ? this.#text('Önceki renkler', 'Previous colors') : this.#text('Önceki ürünler', 'Previous products')));
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('width', '24');
      svg.setAttribute('height', '24');
      svg.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS(svg.namespaceURI, 'path');
      path.setAttribute('d', direction === 'next' ? 'm9 4 8 8-8 8' : 'm15 4-8 8 8 8');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1');
      svg.append(path);
      button.append(svg);
      return button;
    }

    #mountColors(story) {
      const template = story?.querySelector('template[data-story-mount]');
      if (!template) return;
      const track = story.querySelector('.gac-story__track, .gac-story__grid');
      track.append(template.content);
      template.remove();
      if (track.classList.contains('gac-story__grid')) return;
      const update = () => {
        const rect = track.getBoundingClientRect();
        let lastVisible = 0;
        for (const [index, card] of [...track.children].entries()) {
          if (card.getBoundingClientRect().right <= rect.right + 2) lastVisible = index + 1;
        }
        story.querySelector('.gac-story__counter').textContent = `${lastVisible || 1} / ${track.children.length}`;
        story.querySelector('[data-story-arrow="previous"]').disabled = track.scrollLeft <= 2;
        story.querySelector('[data-story-arrow="next"]').disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        const overflow = track.scrollWidth > track.clientWidth + 2;
        for (const arrow of story.querySelectorAll('[data-story-arrow]')) arrow.hidden = !overflow;
      };
      track.addEventListener('scroll', update, { passive: true, signal: this.#carouselEvents.signal });
      window.addEventListener('resize', update, { signal: this.#carouselEvents.signal });
      requestAnimationFrame(update);
    }

    #onClick = event => {
      if (event.target.closest('[data-story-retry]')) this.activate();
      if (event.target.closest('[data-story-back]')) {
        if (this.closest('giesto-collection-hub')) document.querySelector('.gnr [data-gnr-layout="uniform"]')?.click();
        const collection = this.closest('[data-gac-story-enabled]');
        collection?.querySelector('[data-gac-view-option][value="4"]')?.click();
        collection?.querySelector('[data-gac-view-trigger]')?.focus({ preventScroll: true });
      }
      const swatch = event.target.closest('[data-story-color-id]');
      if (swatch) {
        const family = swatch.closest('.gac-story__family');
        const variants = family.querySelector('[data-story-variants]').content;
        const selected = [...variants.children].find(card => card.dataset.storyProduct === swatch.dataset.storyColorId);
        if (selected) {
          const active = family.querySelector('[data-story-active]');
          variants.append(active.firstElementChild);
          active.append(selected);
          for (const button of family.querySelectorAll('[data-story-color-id]')) button.setAttribute('aria-pressed', String(button === swatch));
        }
      }
      const arrow = event.target.closest('[data-story-arrow]');
      if (!arrow) return;
      const track = arrow.closest('.gac-story__colors').querySelector('.gac-story__track');
      const card = track.firstElementChild;
      if (!card) return;
      const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap);
      track.scrollBy({ left: step * (arrow.dataset.storyArrow === 'next' ? 1 : -1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
  }
  customElements.define('giesto-collection-stories', GiestoCollectionStories);
})();
